/**
 * §2.2's recalculation warnings and §8.1's three-dimensional drift, as sentences.
 *
 * **Everything here is a decision that fails silently**, which is why it is a module with tests
 * rather than three conditionals inside a component:
 *
 * - A missing §2.2 warning is a rating that is permanently stale with nothing on screen saying so.
 *   The board computes, the number looks reasonable, and it is simply old.
 * - A warning that fires on the wrong window type is a permanent notice on boards it does not apply
 *   to — the always-on alert this codebase has already decided is worth less than no alert.
 * - A drift notice that merges the dimensions still *looks* right. It says the board is out of
 *   date, which is true, while throwing away the only thing that tells an operator whether to go
 *   and check an import.
 *
 * It computes nothing about a rating. Every figure comes from the server, which is §1.1's
 * projection rule reaching the client.
 */

/** §4.4's window types, as the server reports them on `RatingBoardDto.windowType`. */
export type RatingWindowType = 'rolling' | 'fixed' | 'allTime';

/** `RecalcMode` on the wire. */
export type RatingRecalcMode = 'manual' | 'on_result_entry' | 'scheduled';

export interface RecalcWarning {
  severity: 'warning';

  message: string;

  /**
   * Always `false`, and stated rather than implied. §2.2 is explicit that both of these are
   * warnings and **not** refusals — freezing a rating for an awards night is legitimate, and a
   * screen that blocked it would be wrong about a real use. The field exists so that a component
   * reaching for "should I disable Save" has an answer that says no, rather than deciding for
   * itself.
   */
  blocking: false;
}

/**
 * §2.2's two warnings, or null when there is nothing to say.
 *
 * **Both are about a ROLLING window and only a rolling window.** That is the whole condition: a
 * rolling rating moves with the calendar, so it can go stale with no result having changed. A fixed
 * or all-time board is measured against dates that do not move, so Manual on one is an ordinary
 * choice and `on_result_entry` catches every change there is.
 */
export function recalcWarning(
  mode: RatingRecalcMode | string,
  windowType: RatingWindowType | string,
): RecalcWarning | null {
  if (windowType !== 'rolling') return null;

  if (mode === 'manual') {
    return {
      severity: 'warning',
      blocking: false,
      message:
        'This rating looks back over a rolling window, so it goes out of date as the calendar ' +
        'moves even when no result changes — and on Manual it never recalculates on its own. ' +
        'That is a real choice for a rating you want frozen, for an awards night say, but left ' +
        'set this way the standings will drift further behind every day.',
    };
  }

  if (mode === 'on_result_entry') {
    return {
      severity: 'warning',
      blocking: false,
      message:
        'Recalculating on result entry is not enough for a rolling window. The window moves with ' +
        'the calendar, so this rating goes out of date on days when no result has changed — and ' +
        'nothing will have triggered a recalculation. Add a schedule as well if the standings ' +
        'are being read.',
    };
  }

  return null;
}

/** The three fields a cadence sentence reads, as `RatingBoardDto` carries them. */
export interface CadenceFields {
  recalcMode: RatingRecalcMode | string;
  recalcCron: string | null;
  effectiveRecalcCron: string | null;
  effectiveRecalcNote: string | null;
}

/**
 * What recalculates this rating, in a sentence.
 *
 * **Locked rule 4's distinction is the whole job.** A board storing no cron under `scheduled` is
 * following the *platform's* cadence, resolved when it runs — and a screen showing only the
 * resolved schedule would present that as a choice this room made. That is the stored-versus-chosen
 * confusion Phase 4b removed from room creation, and putting it back on a screen would be the same
 * defect with a nicer font.
 */
export function cadenceLine(board: CadenceFields): string {
  if (board.recalcMode === 'manual') {
    return 'Recalculates only when somebody asks it to.';
  }

  if (board.recalcMode === 'on_result_entry') {
    return 'Recalculates whenever a tournament that could affect it changes — its results, date, tags or buy-in, or it is deleted.';
  }

  if (board.recalcCron) {
    return `Recalculates on this rating’s own schedule (${board.recalcCron}), read in the room’s time zone.`;
  }

  if (board.effectiveRecalcCron) {
    return (
      `Recalculates on the platform’s schedule (${board.effectiveRecalcCron}), read in the room’s ` +
      'time zone. This rating has no schedule of its own, so it follows the platform and moves if ' +
      'the platform does.'
    );
  }

  // Scheduled, inheriting, and the platform's cadence could not be read. NOT "not scheduled",
  // which would be a lie that sends an operator to the wrong place: the rating is scheduled and
  // the platform is what has not been set up.
  return (
    'This rating follows the platform’s schedule, and the platform settings have not been set up ' +
    'yet — so nothing is recalculating it. Ask a platform administrator, or give this rating a ' +
    'schedule of its own.'
  );
}

/** §8.1's three dimensions, as the server reports them. */
export interface DriftFields {
  hasDataDrift: boolean;
  hasAnchorDrift: boolean;

  /**
   * §8.1's third dimension: the board's own §4 settings changed since the snapshot.
   *
   * <b>Null is UNKNOWN, not false.</b> A snapshot written before the configuration was recorded
   * cannot say whether one changed, and answering "unchanged" there would put the original defect
   * back — an operator's own edit reported as a changed result.
   */
  hasConfigurationDrift: boolean | null;

  /** Which blocks moved, in §9.2's section order, as the server names them. */
  configurationChanged: readonly string[];

  daysBehind: number;
  playersAffected: number;
  ranksAffected: number;
  snapshotAsOfDate: string | null;
  currentAsOfDate: string;
}

export interface DriftNotice {
  /** Which dimension. Never merged — see the module note. */
  kind: 'data' | 'anchor' | 'configuration';

  message: string;
}

/**
 * §4's blocks in the words §9.2's own sections use, because "qualification changed" is a schema
 * name and "the minimum to be rated changed" is the thing the operator edited.
 */
const BLOCK_LABELS: Record<string, string> = {
  scope: 'which events it scores',
  cohort: 'which players it rates',
  window: 'how far back it looks',
  aggregation: 'what counts',
  qualification: 'the minimum to be rated',
};

/**
 * §8.1's drift, as **separate notices**.
 *
 * **There is deliberately no merged "out of date" sentence**, and this function returning a list is
 * how that is enforced rather than merely intended. The operator's response differs: data drift may
 * mean an import wants checking — somebody's payout was corrected, or two identities were merged —
 * and anchor drift just means press Recalculate. A single sentence covering both is true and
 * useless.
 *
 * Empty when none has moved, which is the ordinary case. A notice that appeared on every board
 * would stop being read by the time one of them mattered.
 *
 * <b>THREE dimensions, and the third arrived because the report was confidently wrong.</b> A board's
 * §4 settings are editable while Running (§2.4), and a change to them used to be reported as data
 * drift — so raising a minimum said *"a result behind this rating has changed … worth checking the
 * import"*, which sent an operator to audit an import that was fine, beside the self-contradicting
 * *"0 players would be rated differently"*. Both figures were right; the cause was invented.
 *
 * The responses differ, which is the only reason to split any of them: data means check the import,
 * anchor means press Recalculate, configuration means you changed this yourself and here is what
 * recalculating will do.
 */
export function driftNotices(drift: DriftFields): DriftNotice[] {
  const notices: DriftNotice[] = [];

  if (drift.hasAnchorDrift) {
    notices.push({
      kind: 'anchor',
      message:
        `These standings were worked out as of ${drift.snapshotAsOfDate}, which is ` +
        `${days(drift.daysBehind)} ago. This rating looks back over a rolling window, so the ` +
        'window has moved since then and the figures are behind the calendar. Recalculate to ' +
        'bring them up to date.',
    });
  }

  /*
   * CONFIGURATION, and it is the one notice that carries a ZERO without apology. "No player would be
   * rated differently, 335 places would move" is the complete and correct story for a changed
   * minimum: §3.6 keeps qualification out of computation, so no rating moves, and §3.7 ranks an
   * unqualified player below every qualified one, so the order does. Suppressing the zero would
   * leave an operator unable to tell this from a scope change.
   *
   * Null is reported too, and only when there is something to explain — see `configurationNotice`.
   */
  const configuration = configurationNotice(drift);
  if (configuration !== null) notices.push(configuration);

  if (drift.hasDataDrift) {
    // Deliberately not "since the snapshot was taken": the comparison is made at the snapshot's
    // own anchor, so what this reports is genuinely a change in the DATA and never the calendar.
    notices.push({
      kind: 'data',
      message:
        `${people(drift.playersAffected)} would be rated differently on the same dates — a ` +
        'result behind this rating has changed since these standings were published. That is ' +
        'usually a corrected payout, a deal flag or two player records merged. Worth checking ' +
        'the import before recalculating.' +
        (drift.ranksAffected > 0 ? ` ${ranks(drift.ranksAffected)} would move.` : ''),
    });
  }

  return notices;
}

/**
 * §8.1's configuration notice, or null when there is nothing to say.
 *
 * <b>Three states, because the middle one is real.</b> True says which settings moved and what
 * recalculating will do. False says nothing. Null — a snapshot that recorded no configuration, which
 * is every snapshot written before the column existed — says it cannot tell, and says it only when
 * something would move anyway: the state is ordinary on a pre-existing board, so a permanent notice
 * on every one of them would be the always-on alert §8.1 refuses.
 *
 * <b>It never uses the data dimension's words.</b> No result, no import, no payout. Those send a
 * reader somewhere this dimension knows they do not need to go.
 */
function configurationNotice(drift: DriftFields): DriftNotice | null {
  const moving = drift.playersAffected > 0 || drift.ranksAffected > 0;

  if (drift.hasConfigurationDrift === null) {
    if (!moving) return null;

    return {
      kind: 'configuration',
      message:
        'Recalculating would change these standings, and this snapshot was taken before this ' +
        'rating recorded which settings it was worked out with — so whether that is because the ' +
        'board was edited cannot be told from here. ' +
        consequence(drift) +
        ' The next recalculation records it, and this will answer properly from then on.',
    };
  }

  if (drift.hasConfigurationDrift !== true) return null;

  const labels = drift.configurationChanged
    .map((block) => BLOCK_LABELS[block] ?? block)
    .filter((label) => label.length > 0);

  const settings =
    labels.length === 0
      ? 'This rating’s settings have changed'
      : `You changed ${list(labels)}`;

  return {
    kind: 'configuration',
    message:
      `${settings} since these standings were published, so recalculating will apply the new ` +
      `setting${labels.length > 1 ? 's' : ''}. ` +
      consequence(drift) +
      ' Nothing about the results behind this rating has changed.',
  };
}

/**
 * What recalculating would do, in both figures — and a zero is stated rather than omitted, since for
 * a qualification change "no player would be rated differently" IS the answer.
 */
function consequence(drift: DriftFields): string {
  const rated =
    drift.playersAffected === 0
      ? 'No player would be rated differently'
      : `${people(drift.playersAffected)} would be rated differently`;

  return drift.ranksAffected > 0
    ? `${rated}, and ${ranks(drift.ranksAffected)} would move.`
    : `${rated}.`;
}

function list(items: readonly string[]): string {
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function days(count: number): string {
  return count === 1 ? '1 day' : `${count} days`;
}

/** "1 players" is the kind of sentence that makes a reader distrust the rest of the screen. */
function people(count: number): string {
  return count === 1 ? '1 player' : `${count} players`;
}

function ranks(count: number): string {
  return count === 1 ? '1 place' : `${count} places`;
}

/**
 * What §2.4's Start offers on one board — <b>absent, ready, or blocked with a reason.</b>
 *
 * Three answers rather than a boolean, because the panel has to decide three things and this walk
 * has now produced one bug for each of them getting it wrong: a control that is not rendered at all,
 * a control rendered permanently dead, and a sentence naming a step that is not the next step.
 */
export type StartControl =
  /** Not a thing this board can do — render nothing, not a disabled button. */
  | { kind: 'absent' }
  | { kind: 'ready' }
  /** Offered, refusable, and the reason is shown beside it rather than left to the round trip. */
  | { kind: 'blocked'; reason: string };

/**
 * §2.4's Draft → Running — <b>the control that was never built.</b>
 *
 * <b>`POST /{id}/start` shipped in Phase 4 and `useStartRatingBoard` shipped with it; no component
 * has ever referenced that hook, in any commit.</b> So a Draft could not be started through the
 * product — which means no rating could pin a points system, which means no rating could publish
 * standings, which means the feature was unreachable from its own first screen. It survived three
 * phases because every later screen's copy referred to Start as though it existed: the recalculation
 * panel says *Start it to pin its points system*, the standings empty state says *Start it above*.
 * Both were right about what should happen and neither was a control.
 *
 * <b>Absent, not blocked, for a board that has already started.</b> §9.9's rule, which this
 * repository keeps everywhere: a greyed box says "not now" where the truth is "never". A running
 * board has pinned; a closed one is finished; a platform template is cloned into a room and the
 * clone starts, and every write to a template is refused server-side anyway.
 *
 * <b>Blocked carries the reason, never a bare disabled button.</b> §13.3's owed refusal —
 * `points_system_required` — is met here rather than after the round trip, and it names Configure
 * because that is where the fix is.
 *
 * <b>An unrecognised status offers nothing, and that default is the opposite of
 * `publishesStandings`'s on purpose.</b> There, refusing to ask blanks a screen, so the unknown case
 * asks. Here, offering Start performs an <em>irreversible</em> act — §2.4 makes the pin permanent —
 * so the unknown case stays quiet. The cost of each wrong guess is what sets its default.
 */
export function startControl(board: {
  status: string;
  /** Null is a platform template (§2.3): no room, no events, and no write it will accept. */
  roomId: string | null;
  /** §2.4's selection while the board is a Draft, and the pin from the moment it is not. */
  pointsSystemId: string | null;
}): StartControl {
  if (board.roomId === null) return { kind: 'absent' };
  if (board.status !== 'draft') return { kind: 'absent' };

  if (board.pointsSystemId === null) {
    return {
      kind: 'blocked',
      reason:
        'Starting is what pins the points system, so this rating needs one chosen first — ' +
        'Configure, section 2.',
    };
  }

  return { kind: 'ready' };
}
